import { inject, Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { AppInformationService, BrewersUnitOfWork } from './core.module';
import { Invoice, InvoiceStatus, Payment, PaymentCredit } from './model/entity-model';
import moment from 'moment';
import { alert, confirm } from 'devextreme/ui/dialog';
import * as _ from 'lodash';
import { Utility } from './utility';
import { firstValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from 'environments/environment';
import { Response } from './model/response';

@Injectable({
    providedIn: 'root',
})
export class InvoiceService {
    auth = inject(AuthService);
    appInfo = inject(AppInformationService);
    uow = inject(BrewersUnitOfWork);
    http = inject(HttpClient);

    async receivePayment(invoice: Invoice): Promise<boolean> {
        let creditPayment;
        const credits = await this.checkForCredits(invoice);

        if (credits?.length > 0) {
            creditPayment = await this.applyCredits(invoice, credits[0]);
            await this.uow.commit();
            await this.updateBalances([invoice.id]);
        }

        if (!credits || credits.length === 0 || credits[0].amount < invoice.total) {
            return false;
        } else {
            await alert(`Payment Credits applied. Payment Id: ${creditPayment.id} has been updated.`, this.appInfo.appTitle);
            return true;
        }
    }

    async checkForCredits(invoice: Invoice): Promise<PaymentCredit[]> {
        const credits = await this.uow.invoices.getOpenPaymentCredits(invoice.customerId);
        if (!!credits && credits.length > 0) {
            const creditAmount = _.sum(
                credits.map((x) => {
                    return x.amount;
                })
            );
            const result = await confirm(
                `A Payment Credit exists for Customer.  Amount: $${credits[0].amount}.  Do you want to apply it to the selected invoice(s)?`,
                this.appInfo.appTitle
            );
            if (!result) return null;
        }
        return credits;
    }

    async applyCredits(invoice: Invoice, credit: PaymentCredit): Promise<Payment> {
        const invoiceAmount = invoice.unpaidBalance;
        const creditPayment = await this.uow.payments.byId(credit.paymentId, 'paymentLines');
        const creditAmount = credit.amount;
        //create a new payment line for the invoice using the credit
        const paymentLine = this.uow.paymentLines.createEntity({
            paymentId: creditPayment.id,
            invoiceId: invoice.id,
            lineNumber: Payment.getNextLineNumber(creditPayment.paymentLines),
            description: `Inv-${invoice.invoiceNumber} - ${invoice.customer.displayName}`,
            created: new Date(),
            createdBy: this.auth.currentUser.userName,
            amount: invoiceAmount >= creditAmount ? creditAmount : invoiceAmount,
            notes: `Credit Id: ${credit.id} --- Applied ($${creditAmount})`,
        });
        invoice.notes = `Credit Id: ${credit.id} --- ($${creditAmount}) --- Applied ${moment(new Date()).format('MM/DD/yyyy')}`;

        credit.applied = new Date();
        credit.appliedBy = this.auth.currentUser.userName;
        creditPayment.quickBooksLastExport = null;

        //reduce the amount of the overpayment line
        const overPaymentLine = creditPayment.paymentLines.find((x) => {
            if (x.description === 'Invoice OverPayment Record') return x;
        });

        if (!!overPaymentLine) {
            overPaymentLine.amount -= invoiceAmount;
        }

        if (invoiceAmount <= creditAmount) {
            if (invoiceAmount < creditAmount) {
                //add another credit for the difference
                this.uow.paymentCredits.createEntity({
                    customerId: invoice.customerId,
                    paymentId: creditPayment.id,
                    amount: creditAmount - invoiceAmount,
                    created: new Date(),
                    createdBy: this.auth.currentUser.userName,
                });
            }
            // invoice.statusId = InvoiceStatus.Statuses.Paid;
            invoice.paid = new Date();
        } else {
            // invoice.statusId = InvoiceStatus.Statuses.PartiallyPaid;
        }
        return creditPayment;
    }

    async getTaxCodeSummaries(filter: any) {
        filter.startDate = Utility.startOfDay(filter.startDate);
        filter.endDate = Utility.startOfDay(filter.endDate);
        return firstValueFrom(this.http.post<any[]>(`${environment.serviceName}/utility/taxCodeSummaries`, filter));

    }

    async getInvoiceSummaries(filter: any) {
        filter.startDate = Utility.startOfDay(filter.startDate);
        filter.endDate = Utility.startOfDay(filter.endDate);
        return firstValueFrom(this.http.post<any[]>(`${environment.serviceName}/utility/invoiceSummaries`, filter));
    }


    async getInvoiceLineSummaries(filter: any) {
        filter.startDate = Utility.startOfDay(filter.startDate);
        filter.endDate = Utility.startOfDay(filter.endDate);
        return firstValueFrom(this.http.post<any[]>(`${environment.serviceName}/utility/invoiceLineSummaries`, filter));
    }

    async getCategorySummaries(filter: any) {
        filter.startDate = Utility.startOfDay(filter.startDate);
        filter.endDate = Utility.startOfDay(filter.endDate);
        return firstValueFrom(this.http.post<any[]>(`${environment.serviceName}/utility/categorySummaries`, filter));
    }

    async getDailyCategoryTotals(date: Date) {
        return firstValueFrom(this.http.post<any[]>(`${environment.serviceName}/utility/dailyCategoryTotals`, { date }));
    }

    async getProductSummaries(filter: any) {
        filter.startDate = Utility.startOfDay(filter.startDate);
        filter.endDate = Utility.startOfDay(filter.endDate);
        return firstValueFrom(this.http.post<any[]>(`${environment.serviceName}/utility/productSummaries`, filter));
    }

    async createQuickFinanceCharge(invoiceIds: number[]) {
        return firstValueFrom(this.http.post<Response<number>>(`${environment.serviceName}/utility/createQuickFinanceCharge`, { invoiceIds }));
    }

    async createCreditInvoice(invoiceToCreditId: number) {
        return firstValueFrom(this.http.post<Response<number>>(`${environment.serviceName}/utility/createCreditInvoice`, { invoiceToCreditId }));
    }

    async updateBalances(invoiceIds: number[]) {
        await firstValueFrom(this.http.post(`${environment.serviceName}/utility/updateBalances`, { invoiceIds }))
    }

    async getCreditAmount(invoiceId: number) {
        return await firstValueFrom(this.http.get<number>(`${environment.serviceName}/utility/invoiceCreditAmount/${invoiceId}`))
    }

    async getCreditNumbers(invoiceId: number) {
        return await firstValueFrom(this.http.get<{ id: number, invoiceNumber: number }[]>(`${environment.serviceName}/utility/invoiceCreditNumbers/${invoiceId}`))
    }
}
